/**
 * The meeting platform half of an event's location.
 *
 * A virtual event's "location" is a platform, not a place, which the location
 * parser already recognised by keeping such values as unstructured free text.
 * That left every organizer typing the platform their own way -- the catalog has
 * six variations of the same Zoom line. These are the same values as a list.
 *
 * The composed result is still one string, stored in the unstructured `raw`
 * field, so nothing downstream changes: `formatEventLocationDisplay` returns it
 * untouched and existing rows keep rendering exactly as they do now.
 */

/** Offered in the order an organizer is most likely to need them. */
export const EVENT_PLATFORMS = [
  "Zoom",
  "Google Meet",
  "Microsoft Teams Meeting",
  "Other",
] as const;

export type EventPlatform = (typeof EVENT_PLATFORMS)[number];

/** The named platforms, without the catch-all. */
const NAMED_PLATFORMS = EVENT_PLATFORMS.filter(platform => platform !== "Other");

export type EventPlatformParts = {
  /** Empty when nothing has been chosen yet, so a new event starts unselected. */
  platform: EventPlatform | "";
  /** Whatever follows the platform name, or the whole value under "Other". */
  detail: string;
};

/**
 * Splits a stored location into its platform and the detail after it.
 *
 * Matches the longest platform name first, so "Microsoft Teams Meeting" is not
 * read as an unknown value merely because "Microsoft Teams" is not in the list.
 *
 * Anything that does not begin with a known platform is "Other" carrying the
 * whole string. That is what lets an existing free-text location -- a venue, or a
 * platform spelled some other way -- open in the editor without being altered or
 * silently reassigned to a platform it never named.
 */
export function parseEventPlatform(value: string | null | undefined): EventPlatformParts {
  const raw = String(value ?? "").trim();
  if (!raw) return { platform: "", detail: "" };

  const longestFirst = [...NAMED_PLATFORMS].sort((left, right) => right.length - left.length);
  for (const platform of longestFirst) {
    if (raw.toLowerCase().startsWith(platform.toLowerCase())) {
      return { platform, detail: raw.slice(platform.length).trim() };
    }
  }
  return { platform: "Other", detail: raw };
}

/**
 * Rebuilds the single stored string.
 *
 * "Other" contributes no prefix, so choosing it and typing a venue stores the
 * venue alone -- the value a physical location needs, and the value an existing
 * free-text row round-trips to unchanged.
 */
export function composeEventPlatform(parts: EventPlatformParts): string {
  const detail = parts.detail.trim();
  if (!parts.platform) return detail;
  if (parts.platform === "Other") return detail;
  return detail ? `${parts.platform} ${detail}` : parts.platform;
}

/**
 * The detail to keep when the chosen platform changes.
 *
 * The field means two different things either side of "Other": for a named
 * platform it is optional joining information, and for "Other" it is the whole
 * location. Text does not survive a move between those two kinds — carrying
 * "(link sent after registration)" across would save it as the venue, and the
 * reverse would mail a street address out as a joining link. Moving between two
 * named platforms keeps it, because its meaning has not changed.
 */
export function detailAfterPlatformChange(
  previous: EventPlatform | "",
  next: EventPlatform | "",
  detail: string,
): string {
  if (!next) return "";
  if (previous === next) return detail;
  const isWholeLocation = (platform: EventPlatform | "") => platform === "Other";
  return isWholeLocation(previous) === isWholeLocation(next) ? detail : "";
}

/** What a platform needs beyond its name to be described in a form. */
export type EventPlatformMeta = {
  /** Label for the attendee link field, named for the service. */
  linkLabel: string;
  /** A representative link, shown as the field's example rather than a value. */
  linkExample: string;
  /** The host a link for this service is expected to sit on. */
  linkHost: string;
};

/**
 * Per-service copy for the join-link field.
 *
 * "Other" is absent on purpose: it describes a place rather than a service, so it
 * has no link to ask for and keeps the free-text location field instead.
 */
export const EVENT_PLATFORM_META: Record<Exclude<EventPlatform, "Other">, EventPlatformMeta> = {
  Zoom: {
    linkLabel: "Zoom link",
    linkExample: "https://zoom.us/j/1234567890",
    linkHost: "zoom.us",
  },
  "Google Meet": {
    linkLabel: "Google Meet link",
    linkExample: "https://meet.google.com/abc-defg-hij",
    linkHost: "meet.google.com",
  },
  "Microsoft Teams Meeting": {
    linkLabel: "Microsoft Teams link",
    linkExample: "https://teams.microsoft.com/l/meetup-join/19:meeting",
    linkHost: "teams.microsoft.com",
  },
};

/** Whether this platform asks for an attendee link rather than a place. */
export function platformTakesLink(platform: EventPlatform | ""): platform is Exclude<EventPlatform, "Other"> {
  return platform !== "" && platform !== "Other";
}

/**
 * Whether a link looks like it belongs to the chosen service.
 *
 * A warning, never a block: organizations route meetings through vanity domains
 * and tenant-specific hosts, so a mismatch is worth pointing out and not worth
 * refusing.
 */
export function linkMatchesPlatform(platform: EventPlatform | "", link: string): boolean {
  if (!platformTakesLink(platform)) return true;
  const value = link.trim();
  if (!value) return true;
  try {
    return new URL(value).hostname.toLowerCase().endsWith(EVENT_PLATFORM_META[platform].linkHost);
  } catch {
    return false;
  }
}
